import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Waves, Mail, Lock, ShieldCheck, User as UserIcon, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const DEMO_ACCOUNTS = [
  { role: 'Super Admin', email: 'admin@gulfcoastlatino.com', password: 'admin123', icon: ShieldCheck },
  { role: 'DJ / User', email: 'user@gulfcoastlatino.com', password: 'user123', icon: UserIcon },
];

const Login = () => {
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: 'Missing fields', description: 'Enter email and password.' });
      return;
    }
    setBusy(true);
    try {
      const u = await login(email, password);
      toast({ title: `Welcome, ${u.name}`, description: `Signed in as ${u.role.replace('_', ' ')}` });
      navigate(from, { replace: true });
    } catch (err) {
      toast({ title: 'Sign-in failed', description: err?.response?.data?.detail || 'Please try again' });
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex">
      {/* Left showcase */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1470225620780-dba8ba36b745?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxsYXRpbiUyMERKfGVufDB8fHx8MTc4MzYzMzY5MHww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-orange-600/30" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Waves className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-xl leading-none">GULF COAST</div>
              <div className="text-[10px] tracking-[0.32em] text-orange-400 mt-0.5">LATINO</div>
            </div>
          </Link>

          <div>
            <h1 className="font-display text-5xl xl:text-6xl leading-[1.05]">
              The record pool<br />built for Latin DJs.
            </h1>
            <p className="text-neutral-300 mt-4 max-w-md text-sm">
              Sign in to access exclusive mixes, submit tracks, and connect with the Gulf Coast DJ crew.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center">
              <Waves className="w-4 h-4 text-white" />
            </div>
            <div className="font-display text-lg">GULF COAST LATINO</div>
          </div>

          <h2 className="font-display text-3xl">Sign In</h2>
          <p className="text-neutral-400 text-sm mt-2">Welcome back. Enter your credentials to continue.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="gcl-input pl-10"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="gcl-input pl-10 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Demo accounts */}
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Try a demo account</div>
            <div className="space-y-2">
              {DEMO_ACCOUNTS.map((acc) => {
                const Icon = acc.icon;
                return (
                  <button
                    key={acc.email}
                    type="button"
                    onClick={() => fillDemo(acc)}
                    className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-orange-500/40 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-500/15 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-orange-400" />
                      </div>
                      <div>
                        <div className="text-sm text-white">{acc.role}</div>
                        <div className="text-xs text-neutral-500">{acc.email}</div>
                      </div>
                    </div>
                    <span className="text-[11px] text-orange-400 font-semibold">USE</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 text-xs text-neutral-500 text-center">
            <Link to="/" className="hover:text-orange-400 transition-colors">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
